/**
 * The training course's coach (ROUND7 §5; 08 §4 "interactive field manual"): the `MissionCoach` the mission
 * loop mounts, feeds after every sim step and takes down. It says one thing at a time in the trainee's own key
 * names, ticks each lesson off from what the world shows, and never blocks him - a lesson he skips stops being
 * mentioned once its station is behind him.
 *
 * It is also the course's range officer. Nothing on the course can bring the garrison to alarm 3 in the normal
 * way of things (src/gen/training), but two things still can - the Osprey leaving without him, and minutes of
 * gunfire next to a live sentry - and alarm 3 sends ARMED reinforcements 25 s later. Either way the coach says
 * what happened and ends the course after a short beat, long before the first of them could arrive.
 */

import type { Bindings } from '../../input/bindings';
import type { SimEvent, World } from '../../sim/types';
import type { MissionCoach } from '../missionApp';
import type { MissionSettings } from '../missionSettings';
import { interruption } from './interrupts';
import { keyNames } from './keys';
import { CoachPanel } from './panel';
import { CoachProgress } from './progress';

export { CoachProgress, STEPS } from './progress';
export type { StepId } from './stepTypes';
export { settleFlight, stoppedResult, type FlightPorts } from './landing';

/** Why the range officer stopped the course: the end card prints it. */
export const STOP_NOTES = {
  stranded: 'THE OSPREY WAITED AND LEFT WITHOUT YOU. IN THE FIELD YOU WOULD NOW BE ON YOUR OWN, WITH THE WHOLE '
    + 'GARRISON ON FULL ALARM - RECALL COMES ONCE.',
  alarm: 'FULL ALARM: THE GARRISON HEARD SUSTAINED GUNFIRE. IN THE FIELD, ARMED REINFORCEMENTS WOULD NOW BE ON '
    + 'THEIR WAY TO YOU.',
} as const;

/** Sim ticks the stop message stays up before the course ends: long enough to read, far short of the 25 s wave. */
export const STOP_BEAT_TICKS = 240;
/** How long the "DONE" line stays up after a step is ticked. */
const FLASH_TICKS = 90;

export interface TrainingCoachOptions {
  bindings: Bindings;
  settings: Pick<MissionSettings, 'runToggle' | 'crawlToggle' | 'sneakToggle' | 'hud'>;
  /** The course cannot go on. Called once, outside the sim loop, with the words for the end card. */
  onStop(note: string): void;
}

export function createTrainingCoach(options: TrainingCoachOptions): MissionCoach & { progress: CoachProgress } {
  const keys = keyNames(options.bindings, options.settings);
  const progress = new CoachProgress(keys);
  const panel = new CoachPanel(options.settings.hud.textSize ?? 1);
  let root: HTMLElement | null = null;
  let flash = '';
  let flashTicks = 0;
  let stopNote: string | null = null;
  let stopTicks = 0;
  let stopTimer = 0;

  function watchForStop(world: World): void {
    if (world.result !== null) return;
    if (stopNote === null) {
      if (world.stranded) stopNote = STOP_NOTES.stranded;
      else if (world.alert >= 3) stopNote = STOP_NOTES.alarm;
      if (stopNote === null) return;
      stopTicks = STOP_BEAT_TICKS;
    }
    stopTicks--;
    if (stopTicks === 0) {
      const note = stopNote;
      // Not from inside the sim step: ending the course disposes the mission that is calling us.
      stopTimer = window.setTimeout(() => options.onStop(note), 0);
    }
  }

  function render(world: World): void {
    if (root === null) return;
    panel.attach(root, world.insertion.phase !== 'ground');
    if (world.result !== null) {
      panel.show(null);
      return;
    }
    if (stopNote !== null) {
      panel.show({
        head: 'TRAINING - THE RANGE OFFICER', count: '', text: 'THE COURSE IS STOPPED.', note: stopNote,
        urgent: true, flash: '', stopping: true,
      });
      return;
    }
    const line = progress.line();
    const warning = interruption(world, keys);
    // Nothing left to teach in the air once he has steered: the ground lessons wait for the ground. It used to
    // fall through to the X and the Recall key while he was still under the canopy (found playing the course).
    const airborne = world.insertion.phase !== 'ground';
    const idle = airborne ? 'LAND ON OPEN GROUND. THE LESSONS GO ON WHEN YOU ARE DOWN.'
      : `HEAD NORTH TO THE X. ${keys.key('recall')} RECALLS THE OSPREY.`;
    panel.show({
      head: `TRAINING - ${line?.station ?? (airborne ? 'THE DROP' : 'CARRY ON')}`,
      count: line === null ? '' : `${line.index}/${line.count}`,
      text: line?.text ?? idle,
      note: warning ?? line?.note ?? '',
      urgent: warning !== null,
      flash: flashTicks > 0 ? flash : '',
      stopping: false,
    });
  }

  return {
    progress,
    mount(at) {
      root = at;
    },
    tick(world: World, events: readonly SimEvent[]) {
      progress.observe(world, events);
      const done = progress.justDone();
      if (done.length > 0) {
        flash = `DONE: ${done.map((id) => progress.titleOf(id)).join(', ')}`;
        flashTicks = FLASH_TICKS;
      } else if (flashTicks > 0) flashTicks--;
      watchForStop(world);
      render(world);
    },
    dispose() {
      window.clearTimeout(stopTimer);
      panel.dispose();
      root = null;
    },
  };
}
